import React, { useState, useEffect } from 'react';
import { useAuth } from '../Services/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { LoadingSpinner } from './ui/spinner';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { apiFetch } from "../utils/api";
import {
  Download,
  FileText,
  Calendar,
  Receipt,
  Search,
  Plus,
  Clock
} from 'lucide-react';

interface FinancialToolsProps {
  setActiveSection?: (section: string) => void;
}

export function FinancialTools({ setActiveSection }: FinancialToolsProps) {

  const { login, user, token, idToken, currentPartner } = useAuth();

  const [selectedDateRange, setSelectedDateRange] = useState('current-month');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRequestStatementOpen, setIsRequestStatementOpen] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [statementStartDate, setStatementStartDate] = useState('');
  const [statementEndDate, setStatementEndDate] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 10,
  });

  const fetchStatements = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, `/api/statements?partner=${currentPartner.partner_id}&company_id=${currentPartner.company_id}`);
    res.then(async (response) => {
      setStatements(response.data);
    });
  };

  const fetchInvoices = async (page = 1, perPage = 10) => {
    let tk = await idToken();
    let url = `/api/invoices?partner=${currentPartner.partner_id}&page=${page}&per_page=${perPage}&company_id=${currentPartner.company_id}`;
    if (searchTerm) {
      url += `&searchTerm=${searchTerm}`;
    }
    const res = await apiFetch(tk, url);
    setInvoices(res.data);
    setPagination(res.pagination);
  };

  useEffect(() => {
    if (currentPartner) {
      fetchInvoices(1, pagination.perPage);
    }
  }, [currentPartner, searchTerm]);

  useEffect(() => {
    if (currentPartner) {
      fetchStatements();
    }
  }, [currentPartner]);

  const handlePeriodSelection = (period: string) => {
    setSelectedPeriod(period);
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (period) {
      case 'current-month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'last-month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'current-quarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        startDate = new Date(today.getFullYear(), currentQuarter * 3, 1);
        endDate = today;
        break;
      case 'last-quarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const quarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
        startDate = new Date(quarterYear, adjustedQuarter * 3, 1);
        endDate = new Date(quarterYear, adjustedQuarter * 3 + 3, 0);
        break;
      case 'last-6-months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        endDate = today;
        break;
      case 'current-year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      case 'last-year':
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'custom':
        // Don't change dates for custom selection
        return;
      default:
        return;
    }

    if (period !== 'custom') {
      setStatementStartDate(startDate.toISOString().split('T')[0]);
      setStatementEndDate(endDate.toISOString().split('T')[0]);
    }
  };

  const downloadInvoice = async (invoice) => {
    setDownloadingInvoiceId(invoice.id);
    try {
      let tk = await idToken();
      const res = await apiFetch(
        tk,
        `/api/invoices/document?partner_id=${currentPartner.partner_id}&invoice_id=${invoice.invoice_id}&company_id=${invoice.company_id}`,
      );
      downloadBase64File(res.data.ypsReportData, res.data.ypsFileName, "application/pdf");
    } catch (err) {
      console.error(err);
      alert(`Failed to download invoice ${invoice.invoice_id}.`);
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleDownloadSelected = async () => {
    setLoadingInvoice(true);
    for (const invoiceId of selectedInvoices) {
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (invoice) {
        await downloadInvoice(invoice);
        await new Promise(resolve => setTimeout(resolve, 500)); // Delay to prevent browser blocking
      }
    }
    setLoadingInvoice(false);
    setSelectedInvoices([]);
  };

  function downloadBase64File(base64Data, filename, mimeType) {
    // Convert base64 to raw binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create a Blob and object URL
    const blob = new Blob([byteArray], { type: mimeType });
    const url = URL.createObjectURL(blob);

    // Create a temporary link to trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    // Cleanup
    URL.revokeObjectURL(url);
  }

  const downloadStatement = async (statement) => {
    console.log(`downloading statement : ${statement.id}`)
    setLoadingInvoice(true);
    setTimeout(async () => {
      try {
        const res = await apiFetch(await idToken(), `/api/statements/document`, {
          method: "POST",
          body: JSON.stringify({
            company_id: currentPartner.company_id,
            partner_id: currentPartner.partner_id,
            statement_id: statement.id
          })
        });
        const response = await res;
        downloadBase64File(response.data.reportData, response.data.fileName, "application/pdf");
        fetchStatements();
      } catch (err) {
        console.error(err);
        alert("Failed to download invoice.");
      } finally {
        setLoadingInvoice(false);
      }
    }, 0);

  };

  const handleRequestStatement = () => {
    if (!statementStartDate) {
      alert('Please select a start date for the statement.');
      return;
    }

    if (!statementEndDate) {
      alert('Please select an end date for the statement.');
      return;
    }

    if (new Date(statementStartDate) > new Date(statementEndDate)) {
      alert('Start date must be before end date.');
      return;
    }

    console.log('Requesting statement from:', statementStartDate, 'to:', statementEndDate);

    setLoadingInvoice(true);
    setTimeout(async () => {
      try {
        const res = await apiFetch(await idToken(), `/api/statements/document`, {
          method: "POST",
          body: JSON.stringify({
            start_date: statementStartDate,
            end_date: statementEndDate,
            partner_id: currentPartner.partner_id,
            company_id: currentPartner.company_id
          })
        });
        const response = await res;
        downloadBase64File(response.data.reportData, `Statement.pdf`, "application/pdf");
        fetchStatements();
      } catch (err) {
        console.error(err);
        alert("Failed to download invoice.");
      } finally {
        setLoadingInvoice(false);
      }
    }, 0);

    // Reset form and close dialog
    setStatementStartDate('');
    setStatementEndDate('');
    setSelectedPeriod('');
    setIsRequestStatementOpen(false);

  };

  const renderPageNumbers = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    const pageNumbers = new Set();
    pageNumbers.add(1);
    pageNumbers.add(total_pages);

    for (let i = current_page - 1; i <= current_page + 1; i++) {
      if (i > 0 && i <= total_pages) {
        pageNumbers.add(i);
      }
    }

    const sortedPages = Array.from(pageNumbers).sort((a, b) => a - b);
    const renderedPages = [];
    let lastPage = 0;

    for (const page of sortedPages) {
      if (lastPage > 0 && page - lastPage > 1) {
        renderedPages.push('...');
      }
      renderedPages.push(page);
      lastPage = page;
    }

    return renderedPages.map((page, index) =>
      typeof page === 'number' ? (
        <Button
          key={index}
          variant={current_page === page ? 'default' : 'outline'}
          size="sm"
          onClick={() => fetchInvoices(page, pagination.per_page)}
          className={current_page === page ? 'bg-worldsview-blue hover:bg-worldsview-blue/90' : ''}
          disabled={loadingInvoice}
        >
          {page}
        </Button>
      ) : (
        <span key={index} className="px-2 py-1 text-sm flex items-center">
          {page}
        </span>
      )
    );
  };

  return (
    <>

      <div className="space-y-6 animate-fade-in-fast">

        {/* Header */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1>Finance</h1>
              <p className="text-muted-foreground mt-2">
                Manage invoices and statements
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="invoices" className="space-y-6">
          <div className="glass-card">
            <CardContent className="pt-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="invoices" className="flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Invoices
                </TabsTrigger>
                <TabsTrigger value="statements" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Statements
                </TabsTrigger>
              </TabsList>
            </CardContent>
          </div>
          {loadingInvoice && <LoadingSpinner message='Generating document, please wait or continue working in a new tab...' />}

          {/* Invoices Tab */}
          {!loadingInvoice &&
            <TabsContent value="invoices" className="space-y-6">
              <Card className="glass-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-worldsview-blue" />
                      Invoice Management
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleDownloadSelected}
                        disabled={selectedInvoices.length === 0}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Selected ({selectedInvoices.length})
                      </Button>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search invoices..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 glass-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-hidden rounded-lg border border-border/50">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/20">
                          <TableHead>
                            <Checkbox
                              checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedInvoices(invoices.map(invoice => invoice.id));
                                } else {
                                  setSelectedInvoices([]);
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead>Invoice ID</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Opportunity ID</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoices.map((invoice) => (
                          <TableRow key={invoice.id} className="hover:bg-muted/10">
                            <TableCell>
                              <Checkbox
                                checked={selectedInvoices.includes(invoice.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedInvoices([...selectedInvoices, invoice.id]);
                                  } else {
                                    setSelectedInvoices(selectedInvoices.filter(id => id !== invoice.id));
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-sm">{invoice.invoice_id}</TableCell>
                            <TableCell className="font-medium">{invoice.invoicing_name}</TableCell>
                            <TableCell>
                              <span className="font-mono text-sm text-muted-foreground">{invoice.sales_id}</span>
                            </TableCell>
                            <TableCell className="font-medium text-right">
                              {
                                new Intl.NumberFormat(undefined,
                                  {
                                    style: "currency",
                                    currency: invoice.currency_code
                                  }).format(invoice.amount)
                              }
                            </TableCell>
                            <TableCell>{new Date(invoice.invoice_date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="glass-button" 
                                  onClick={() => downloadInvoice(invoice)}
                                  disabled={downloadingInvoiceId === invoice.id}
                                >
                                  {downloadingInvoiceId === invoice.id ? (
                                    <>
                                      <div className="w-4 h-4 mr-1 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                      Loading...
                                    </>
                                  ) : (
                                    <>
                                      <Download className="w-4 h-4 mr-1" />
                                      PDF
                                    </>
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {invoices.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No invoices found matching your search criteria.
                    </div>
                  )}

                  <div className="grid grid-cols-3 items-center p-4">
                    <div className="text-left">
                      <p className="text-sm text-muted-foreground">
                        Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} invoices)
                      </p>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchInvoices(pagination.current_page - 1, pagination.per_page)}
                        disabled={pagination.current_page <= 1 || loadingInvoice}
                      >
                        Previous
                      </Button>
                      {renderPageNumbers()}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchInvoices(pagination.current_page + 1, pagination.per_page)}
                        disabled={pagination.current_page >= pagination.total_pages || loadingInvoice}
                      >
                        Next
                      </Button>
                    </div>
                    <div className="text-right"></div> {/* Empty div for alignment */}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          }

          {/* Statements Tab */}
          {!loadingInvoice &&
            <TabsContent value="statements" className="space-y-6">
              <Card className="glass-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-worldsview-blue" />
                      Monthly Statements
                    </CardTitle>
                    <Dialog open={isRequestStatementOpen} onOpenChange={setIsRequestStatementOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-worldsview-purple hover:bg-worldsview-purple/90">
                          <Plus className="w-4 h-4 mr-2" />
                          Request Statement
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="glass-modal sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle>Request Partner Statement</DialogTitle>
                          <DialogDescription>
                            Request your partner statement for a date range.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-6 py-4">
                          <div className="bg-blue-50/50 p-3 rounded-lg border border-worldsview-blue/20">
                            <div className="flex items-start gap-2">
                              <Clock className="w-4 h-4 text-worldsview-blue mt-0.5" />
                              <div className="text-sm">
                                <p className="font-medium text-worldsview-blue">Processing Information</p>
                                <p className="text-muted-foreground mt-1">
                                  Statement generation typically takes about a few minutes. You'll receive a notification when your statement is ready for download.
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="period-select">Statement Period</Label>
                              <Select
                                value={selectedPeriod}
                                onValueChange={handlePeriodSelection}
                              >
                                <SelectTrigger className="glass-input">
                                  <SelectValue placeholder="Select a period or choose custom dates" />
                                </SelectTrigger>
                                <SelectContent className="glass-modal">
                                  <SelectItem value="current-month">Current Month</SelectItem>
                                  <SelectItem value="last-month">Last Month</SelectItem>
                                  <SelectItem value="current-quarter">Current Quarter</SelectItem>
                                  <SelectItem value="last-quarter">Last Quarter</SelectItem>
                                  <SelectItem value="last-6-months">Last 6 Months</SelectItem>
                                  <SelectItem value="current-year">Current Year</SelectItem>
                                  <SelectItem value="last-year">Last Year</SelectItem>
                                  <SelectItem value="custom">Custom Date Range</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                Choose a preset period or select custom for manual date entry
                              </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="statement-start-date">Statement Start Date *</Label>
                                <Input
                                  id="statement-start-date"
                                  type="date"
                                  value={statementStartDate}
                                  onChange={(e) => {
                                    setStatementStartDate(e.target.value);
                                    setSelectedPeriod('custom');
                                  }}
                                  className="glass-input"
                                  max={statementEndDate || new Date().toISOString().split('T')[0]}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="statement-end-date">Statement End Date *</Label>
                                <Input
                                  id="statement-end-date"
                                  type="date"
                                  value={statementEndDate}
                                  onChange={(e) => {
                                    setStatementEndDate(e.target.value);
                                    setSelectedPeriod('custom');
                                  }}
                                  className="glass-input"
                                  min={statementStartDate}
                                  max={new Date().toISOString().split('T')[0]}
                                />
                              </div>
                            </div>

                            {selectedPeriod && selectedPeriod !== 'custom' && (
                              <div className="text-sm text-muted-foreground bg-blue-50/50 p-3 rounded-lg border border-worldsview-blue/20">
                                <Calendar className="w-4 h-4 inline mr-2 text-worldsview-blue" />
                                Selected period: <strong>{selectedPeriod.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                                {statementStartDate && statementEndDate && (
                                  <span className="block mt-1">
                                    From {new Date(statementStartDate).toLocaleDateString()} to {new Date(statementEndDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 pt-4">
                            <Button
                              onClick={handleRequestStatement}
                              disabled={!statementStartDate || !statementEndDate}
                              className="flex-1 bg-worldsview-purple hover:bg-worldsview-purple/90"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Request Statement
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setIsRequestStatementOpen(false)}
                              className="glass-button"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-hidden rounded-lg border border-border/50">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/20">
                          <TableHead>From Date</TableHead>
                          <TableHead>To Date</TableHead>
                          <TableHead>Requested by</TableHead>
                          <TableHead>Requested Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {statements.map((statement) => (
                          <TableRow key={statement.id} className="hover:bg-muted/10">
                            <TableCell className="font-medium">{new Date(statement.from_date).toLocaleDateString()}</TableCell>
                            <TableCell className="font-medium">{new Date(statement.to_date).toLocaleDateString()}</TableCell>
                            <TableCell>{statement.requested_by.first_name} {statement.requested_by.last_name}</TableCell>
                            <TableCell>{new Date(statement.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" className="glass-button" onClick={() => downloadStatement(statement)}>
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          }

        </Tabs>
      </div >

    </>

  );
}